import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADDRESS } from 'services/consts';

import { getCityByCountry, getCountryList, getDistrictByCity, getStateByCountry } from 'stores/globalAddressSlice';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import CityForm from './CityForm';
import CountryForm from './CountryForm';
import DistrictForm from './DistrictForm';
import StateForm from './StateForm';
import TagChips from './TagChips';

const defaultColSpan = {
	md: 6
};

const NeighbourhoodForm = NiceModal.create((props) => {
	const { mode = 'add', modalName, row } = props;
	const [countries, setCountries] = useState([]);
	const [cities, setCities] = useState([]);
	const [states, setStates] = useState([]);
	const [districts, setDistricts] = useState([]);
	const [tags, setTags] = useState([]);
	const axios = useAxiosPrivate();
	const { showError } = useAlert();
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().required(),
					countryName: yup.string().nullable().required(),
					countryID: yup.number().nullable(),
					stateID: yup.number().nullable(),
					stateName: yup.string().nullable(),
					cityID: yup.number().nullable(),
					cityName: yup.string().nullable().required(),
					districtID: yup.number().nullable(),
					districtName: yup.string().nullable().required()
				})
				.required(),
		[]
	);
	const fillForm = useCallback(async () => {
		const defaultValues = {
			name: null,
			countryID: null,
			countryName: null,
			stateID: null,
			stateName: null,
			cityID: null,
			cityName: null,
			districtID: null,
			districtName: null
		};
		fetchCountries();
		if (!row?.id) return defaultValues;
		fetchNeighbourhoodTags(row.id);
		fetchStatesByCountry(row.countryName);
		fetchCitiesByCountry(row.countryName);
		fetchDistrictsByCity(row.cityName);
		return {
			...defaultValues,
			...row
		};
	}, [row]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);
	const isLoading = isSubmitting || isFormLoading;
	const watchedCountryName = watch('countryName');
	const watchedStateName = watch('stateName');
	const watchedCityName = watch('cityName');
	const watchedDistrictName = watch('districtName');

	const onSubmit = useCallback(
		async (data) => {
			try {
				await axios.post(ADDRESS.SAVE_NEIGHBORHOOD, data);
				dispatch(updateDynamicListData(DYNAMICLISTS.NEIGHBORHOODS));
				handleCloseModal();
			} catch (error) {
				if (error?.response?.data?.code === 'error.AD002') {
					showError({
						message: t('error.AD002', {
							value: t('neighbourhood')
						})
					});
				} else {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
					AppLogger.error(error);
				}
			}
		},
		[row]
	);

	const fetchCountries = useCallback(async () => {
		await dispatch(getCountryList())
			.unwrap()
			.then((response) => {
				setCountries(response);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: 'noDataFound'
				});
				setCountries([]);
			});
	}, []);

	const fetchStatesByCountry = useCallback(async (name) => {
		await dispatch(getStateByCountry(name))
			.unwrap()
			.then((response) => {
				setStates(response?.data ?? []);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: 'noDataFound'
				});
				setStates([]);
			});
	}, []);

	const handleCountryChange = useCallback((value) => {
		setValue('stateName', null);
		setValue('stateID', null);
		setValue('cityName', null);
		setValue('cityID', null);
		setValue('districtName', null);
		setValue('districtID', null);
		setValue('countryName', value, { shouldValidate: true, shouldDirty: true });
		setValue('countryID', countries.find((country) => country.shortCode === value)?.id);
		fetchStatesByCountry(value);
		fetchCitiesByCountry(value);
	}, []);

	const fetchCitiesByCountry = useCallback(async (name) => {
		await dispatch(getCityByCountry(name))
			.unwrap()
			.then((response) => {
				setCities(response?.data ?? []);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: 'noDataFound'
				});
				setCities([]);
			});
	}, []);

	const fetchDistrictsByCity = useCallback(
		async (name) => {
			await dispatch(getDistrictByCity(name))
				.unwrap()
				.then((response) => {
					setDistricts(response?.data ?? []);
				})
				.catch((error) => {
					AppLogger.error(error);
					showError({
						message: 'noDataFound'
					});
					setDistricts([]);
				});
		},
		[watchedCityName]
	);

	const fetchNeighbourhoodTags = useCallback(async (id) => {
		await axios
			.post(ADDRESS.GET_NEIGHBOURHOOD_TAG_LIST, {
				parentID: id
			})
			.then((res) => {
				if (Array.isArray(res.data.data)) {
					setTags(res.data.data ?? []);
				} else {
					setTags([]);
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			});
	}, []);

	return (
		<Modal fullwidth name={modalName} maxWidth="x-large" className="modal-neighbourhood">
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'add' ? 'addNeighbourhood' : mode === 'edit' ? 'editNeighbourhood' : 'viewNeighbourhood'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="name" colSpan={defaultColSpan} isLoading={isLoading} required>
						<StringInput name="name" disabled={mode === 'view'} />
					</FormItem>
					{/* Country */}
					<FormItem colSpan={defaultColSpan} label="country" required isLoading={isLoading}>
						<EnumSelect
							name="countryName"
							labelPrefix="country."
							data={countries?.map((country) => country.shortCode) ?? []}
							setValue={handleCountryChange}
							value={watchedCountryName}
							valueRemovable={false}
							disabled={mode === 'view'}
							firstItem={{
								action: () =>
									NiceModal.show(CountryForm, {
										setCountry: (value) => {
											setValue('countryName', value.shortCode ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('countryID', value.id ?? null);
										}
									}),
								label: <Typography color="var(--blue)">addCountry</Typography>
							}}
						/>
					</FormItem>
					{/* State */}
					<FormItem colSpan={defaultColSpan} label="state" isLoading={isLoading}>
						<EnumSelect
							name="stateName"
							data={states?.map((state) => state.name)}
							setValue={(value) => {
								setValue('cityName', null);
								setValue('cityID', null);
								setValue('districtName', null);
								setValue('districtID', null);
								setValue('stateName', value, { shouldValidate: true, shouldDirty: true });
								setValue('stateID', states.find((state) => state.name === value)?.id);
							}}
							value={watchedStateName}
							disabled={mode === 'view' || !watchedCountryName}
							firstItem={{
								action: () =>
									NiceModal.show(StateForm, {
										setState: (value) => {
											setValue('stateName', value.name ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('stateID', value.id ?? null);
										}
									}),
								label: <Typography color="var(--blue)">addState</Typography>
							}}
						/>
					</FormItem>
					{/* City */}
					<FormItem colSpan={defaultColSpan} label="city" isLoading={isLoading} required>
						<EnumSelect
							name="cityName"
							data={cities?.map((city) => city.name)}
							setValue={(value) => {
								setValue('districtName', null);
								setValue('districtID', null);
								setValue('cityName', value, { shouldValidate: true, shouldDirty: true });
								setValue('cityID', cities.find((city) => city.name === value)?.id);
								fetchDistrictsByCity(value);
							}}
							value={watchedCityName}
							disabled={mode === 'view' || !watchedCountryName}
							firstItem={{
								action: () =>
									NiceModal.show(CityForm, {
										setCity: (value) => {
											setValue('cityName', value.name ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('cityID', value.id ?? null);
										}
									}),
								label: <Typography color="var(--blue)">addCity</Typography>
							}}
						/>
					</FormItem>
					{/* District */}
					<FormItem colSpan={defaultColSpan} label="district" isLoading={isLoading} required>
						<EnumSelect
							name="districtName"
							data={districts?.map((district) => district.name)}
							setValue={(value) => {
								setValue('districtName', value, { shouldValidate: true, shouldDirty: true });
								setValue('districtID', districts.find((district) => district.name === value)?.id);
							}}
							value={watchedDistrictName}
							disabled={mode === 'view' || !watchedCityName}
							firstItem={{
								action: () =>
									NiceModal.show(DistrictForm, {
										setDistrict: (value) => {
											setValue('districtName', value.name ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('districtID', value.id ?? null);
										}
									}),
								label: <Typography color="var(--blue)">addDistrict</Typography>
							}}
						/>
					</FormItem>
					{mode !== 'add' && (
						<FormItem label="tags" childStyle={{ margin: 0 }} isLoading={isLoading}>
							<TagChips
								tags={tags}
								setTags={setTags}
								parentID={row?.id}
								type="neighbourhood"
								disabled={mode === 'view'}
								isLoading={isLoading}
							/>
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{mode !== 'view' && (
					<Button
						label={mode === 'add' ? 'add' : 'update'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit)}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default NeighbourhoodForm;
